import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { type EditorState, Plugin, PluginKey, type Selection } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { Extension } from "@tiptap/react";

const SECTION_HEADER_LABEL_SELECTOR = "[data-df-section-header-label]";
const FIRST_HEADER_SELECTED_META = "selected";
const FIRST_HEADER_PENDING_MOUSE_META = "pendingMouseSelection";

export interface FirstSectionHeaderSelectionState {
  selected: boolean;
  pendingMouseSelection: boolean;
}

export const firstSectionHeaderSelectionPluginKey = new PluginKey<FirstSectionHeaderSelectionState>(
  "firstSectionHeaderSelection"
);

export function firstSectionHeaderIsSelected(state: EditorState): boolean {
  return firstSectionHeaderSelectionPluginKey.getState(state)?.selected === true;
}

function firstTopLevelSection(doc: ProseMirrorNode): ProseMirrorNode | null {
  const node = doc.firstChild;
  if (
    node?.type.name === "sectionTitle" ||
    node?.type.name === "paragraphSection" ||
    node?.type.name === "richSection"
  ) {
    return node;
  }
  return null;
}

function firstEditableContentStart(doc: ProseMirrorNode): number | null {
  const firstSection = firstTopLevelSection(doc);
  if (!firstSection) return null;

  if (firstSection.type.name === "sectionTitle") {
    return 1;
  }

  const firstChild = firstSection.firstChild;
  if (!firstChild?.isTextblock) {
    return null;
  }

  return 2;
}

function selectionTouchesFirstEditableBoundary(
  doc: ProseMirrorNode,
  selection: Selection
): boolean {
  const firstContentStart = firstEditableContentStart(doc);
  if (firstContentStart === null) return false;

  return selection.from <= firstContentStart && selection.to >= firstContentStart;
}

function firstHeaderFromView(view: EditorView): HTMLElement | null {
  const firstSection = firstTopLevelSection(view.state.doc);
  const fieldId = typeof firstSection?.attrs.fieldId === "string" ? firstSection.attrs.fieldId : "";
  if (!fieldId) return null;

  return (
    Array.from(view.dom.querySelectorAll<HTMLElement>(SECTION_HEADER_LABEL_SELECTOR)).find(
      (element) => element.dataset.fieldId === fieldId
    ) ?? null
  );
}

function mouseStartsAtFirstHeaderBoundary(view: EditorView, event: MouseEvent): boolean {
  const header = firstHeaderFromView(view);
  if (!header) return false;

  if (event.target instanceof Node && header.contains(event.target)) {
    return true;
  }

  const rect = header.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  return (
    event.clientY <= rect.bottom &&
    event.clientY >= rect.top - 12 &&
    event.clientX >= rect.left - 8 &&
    event.clientX <= rect.right + 8
  );
}

function setFirstHeaderSelection(
  view: EditorView,
  selected: boolean,
  pendingMouseSelection = false
): void {
  view.dispatch(
    view.state.tr.setMeta(firstSectionHeaderSelectionPluginKey, {
      [FIRST_HEADER_SELECTED_META]: selected,
      [FIRST_HEADER_PENDING_MOUSE_META]: pendingMouseSelection,
    })
  );
}

function buildDecorations(state: FirstSectionHeaderSelectionState, doc: ProseMirrorNode) {
  if (!state.selected) {
    return DecorationSet.empty;
  }

  const firstSection = firstTopLevelSection(doc);
  if (!firstSection) {
    return DecorationSet.empty;
  }

  return DecorationSet.create(doc, [
    Decoration.node(0, firstSection.nodeSize, {
      class: "df-first-section-header-selected",
    }),
  ]);
}

export const FirstSectionHeaderSelection = Extension.create({
  name: "firstSectionHeaderSelection",

  addProseMirrorPlugins() {
    return [
      new Plugin<FirstSectionHeaderSelectionState>({
        key: firstSectionHeaderSelectionPluginKey,
        state: {
          init: () => ({ selected: false, pendingMouseSelection: false }),
          apply(transaction, previous) {
            const meta = transaction.getMeta(firstSectionHeaderSelectionPluginKey) as
              | Partial<FirstSectionHeaderSelectionState>
              | undefined;

            if (meta) {
              return {
                selected: typeof meta.selected === "boolean" ? meta.selected : previous.selected,
                pendingMouseSelection:
                  typeof meta.pendingMouseSelection === "boolean"
                    ? meta.pendingMouseSelection
                    : previous.pendingMouseSelection,
              };
            }

            if (transaction.docChanged || transaction.selectionSet) {
              return { selected: false, pendingMouseSelection: false };
            }

            return previous;
          },
        },
        props: {
          decorations(state) {
            return buildDecorations(
              firstSectionHeaderSelectionPluginKey.getState(state) ?? {
                selected: false,
                pendingMouseSelection: false,
              },
              state.doc
            );
          },
          handleKeyDown(view, event) {
            const pluginState = firstSectionHeaderSelectionPluginKey.getState(view.state);

            if (event.key === "Escape" && pluginState?.selected) {
              event.preventDefault();
              setFirstHeaderSelection(view, false);
              return true;
            }

            if (
              (event.key === "Backspace" || event.key === "Delete") &&
              pluginState?.selected &&
              view.state.selection.empty
            ) {
              event.preventDefault();
              setFirstHeaderSelection(view, false);
              return true;
            }

            if (
              event.key !== "ArrowUp" ||
              !event.shiftKey ||
              !selectionTouchesFirstEditableBoundary(view.state.doc, view.state.selection)
            ) {
              return false;
            }

            event.preventDefault();
            setFirstHeaderSelection(view, true);
            return true;
          },
          handleDOMEvents: {
            mousedown(view, event) {
              if (!(event instanceof MouseEvent)) return false;

              setFirstHeaderSelection(view, false, mouseStartsAtFirstHeaderBoundary(view, event));
              return false;
            },
            mouseup(view) {
              const pluginState = firstSectionHeaderSelectionPluginKey.getState(view.state);
              if (!pluginState?.pendingMouseSelection) return false;

              setFirstHeaderSelection(view, true);
              return false;
            },
            blur(view) {
              const pluginState = firstSectionHeaderSelectionPluginKey.getState(view.state);
              if (!pluginState?.selected && !pluginState?.pendingMouseSelection) return false;

              setFirstHeaderSelection(view, false);
              return false;
            },
          },
        },
      }),
    ];
  },
});
